import clsx from 'clsx';
import styles from './PatientsSection.module.scss';
import { FC } from 'react';
import { useGetPatientsSummaryQuery } from 'src/services/api/api-client/StudyQuery';
import { Placeholder, StudyInfoPageSection } from '../../layoutComponents/StudyInfoPageLayout';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { IPatientGroupSummaryDto } from 'src/services/api/api-client';
import { AppProgress } from 'uikit/progress/AppProgress';
import { AppPieChart, PieChartData } from 'uikit/appPieChart/AppPieChart';
import { ReactComponent as ClipboardIcon } from 'assets/img/common/il_g_clipboard_patient_196.svg';
import { Tooltip } from 'uikit/tooltip/tooltip.component';

export const PatientsSection = () => {
  const { id } = useStudy() ?? {};
  const { t } = useScopedTranslation('StudyInfoPage.PatientsSection');
  const query = useGetPatientsSummaryQuery({ studyId: id! }, { keepPreviousData: false, refetchOnMount: 'always' });
  const maxPatientsPerGroupCount = Math.max(
    ...(query.data?.flatMap((g) => g.patientsPerProgressStepCounters.map((p) => p.patientCount)) ?? []),
  );

  const noPatients = query.data?.every((g) => !g.patientsPerStudyStateCounters.patientCount);
  const noGroups = !query.data?.length;

  return (
    <StudyInfoPageSection
      title={t('Title')}
      variant={'flex'}
      isLoading={query.isLoading}
      isFullHeight={true}
      placeholderProps={{
        Icon: ClipboardIcon,
        text: t('Placeholder'),
        showPlaceholder: noGroups || noPatients,
      }}
    >
      <div className={styles.sectionContainer}>
        {query.data?.map((g) => (
          <GroupSection
            key={g.groupId}
            {...g}
            maxPatientsPerGroupCount={maxPatientsPerGroupCount}
            isSingleGroup={query.data.length === 1}
          />
        ))}
      </div>
    </StudyInfoPageSection>
  );
};

export const GroupSection: FC<
  IPatientGroupSummaryDto & { maxPatientsPerGroupCount: number; isSingleGroup?: boolean }
> = ({
  groupTitle,
  patientsPerStudyStateCounters,
  patientsPerProgressStepCounters,
  maxPatientsPerGroupCount,
  isSingleGroup,
}) => {
  const { t } = useScopedTranslation('StudyInfoPage.PatientsSection');
  const { notStarted, inProgress, finished, archived, droppedOut, patientCount } = patientsPerStudyStateCounters;

  const data: PieChartData[] = [
    { name: 'NotStarted', value: notStarted, color: '#FDAC94' },
    { name: 'InProgress', value: inProgress, color: '#BBC4EE' },
    { name: 'Finished', value: finished, color: '#92D6C5' },
    { name: 'Archived', value: archived, color: '#F8DF7E' },
    { name: 'DroppedOut', value: droppedOut, color: '#BFC5C5' },
  ];

  const isEmpty = !patientsPerStudyStateCounters.patientCount;

  return (
    <div className={styles.groupContainer}>
      <div className={styles.groupTitle}>{groupTitle}</div>
      <Placeholder
        showPlaceholder={isEmpty}
        Icon={ClipboardIcon}
        text={t('Placeholder')}
        className={styles.groupPlaceholder}
      >
        <div className={clsx(styles.flex, { [styles.grid]: isSingleGroup })}>
          <div className={styles.groupChart}>
            <div className={styles.chartTextContainer}>
              <div className={styles.chartTitle}>{t('ChartCounter.InProgress')}</div>
              <div className={styles.chartCounter}>
                <div>{inProgress}</div>
                <div>{`/${patientCount}`}</div>
              </div>
            </div>
            <AppPieChart data={data} />
          </div>
          <div className={styles.studyStateCounterContainer}>
            {data?.map((x) => (
              <div key={x.name} className={styles.studyStateCounterRow}>
                <div className={styles.box} style={{ backgroundColor: x.color }} />
                <div key={x.name} className={clsx(styles.counter)}>
                  {t(`StudyStateCounters.${x.name}`, `${x.name} (${x.value})`, { count: x.value })}
                </div>
              </div>
            ))}
          </div>
          <div className={styles.patientProgressContainer}>
            <div className={styles.title}>{t('PatientProgressCounters.Title')}</div>
            <div className={styles.rows}>
              {patientsPerProgressStepCounters.map((x) => (
                <div className={styles.counterRow} key={x.shortStepName}>
                  <AppProgress
                    className={styles.progressBar}
                    color={'#bbc4ee'}
                    value={x.patientCount}
                    max={maxPatientsPerGroupCount}
                  />
                  <div className={styles.counter}>
                    <Tooltip text={x.stepName}>
                      {t('PatientProgressCounters.PatientProgressCountersPattern', {
                        stepName: x.shortStepName,
                        count: x.patientCount,
                      })}
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Placeholder>
    </div>
  );
};
